import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Form, Input, Modal, Segmented, Select, Tag, message,
} from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import _ from 'lodash';
import { parseFullName } from 'parse-full-name';

import { getRuntimeFeatures } from '../../utils/features-runtime';
import { languageOptions, langName } from '../../utils/languages';
import session from '../../utils/session';
import resources from '../../reducers/inbox/resources';
import { getContactByMobile, checkCanSendNumber } from '../../reducers/contact/resources';
import { getWhatsAppSenders, WhatsAppSender } from '../../reducers/whatsapp/resources';
import PhoneInputUS from '../common/PhoneInputUS';
import ChannelIcon from '../common/ChannelIcon';
import MessageInputField from '../inbox-v2/components/MessageInputField/MessageInputField';
import WhatsAppTemplatePanel, { WhatsAppTemplateSend } from './WhatsAppTemplatePanel';

type NewMessageChannel = 'sms' | 'whatsapp';

/** 'askinput' until a full number is typed, 'checking' while /cansend runs, then the verdict. */
type CanSend = 'askinput' | 'checking' | boolean;

interface MobileStatus {
  status: '' | 'validating' | 'success' | 'error';
  help: string | null;
}

export interface CalendarInvitePayload {
  to: string;
  firstname: string;
  lastname: string;
  customMessage?: string;
  schedulingUserId?: string;
  translation?: any;
}

export interface NewMessageModalProps {
  open: boolean;
  onClose: () => void;
  templates: any[];
  sequenceTemplates: any[];
  userHasCalendar: () => Promise<any[]>;
  /** Called when the composer asks to connect a calendar; the caller closes the modal and navigates. */
  onCalendarSetup: () => void;
  /** Sends the interview-scheduling invite; the modal closes itself when this resolves. */
  sendCalendarInvite(payload: CalendarInvitePayload): Promise<void>;
}

const INITIAL_STATUS: MobileStatus = { status: '', help: null };

/**
 * "Send New Message" dialog, extracted from the default layout.
 *
 * Phone entry → /api/v1/numbers/cansend + /api/v1/contacts/by-mobile lookup
 * (debounced) → name fields (locked for an existing contact) → ADR-035
 * candidate language → the shared inbox composer, which posts
 * `sendType: 'new-message'` to /api/v1/conversation/send.
 */
const NewMessageModal: React.FC<NewMessageModalProps> = ({
  open,
  onClose,
  templates,
  sequenceTemplates,
  userHasCalendar,
  onCalendarSetup,
  sendCalendarInvite,
}) => {
  const [tonumber, setTonumber] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [isContactExists, setIsContactExists] = useState(false);
  /** Id of the existing contact on this number (WhatsApp template preview). */
  const [existingContactId, setExistingContactId] = useState<string | null>(null);
  const [cansend, setCansend] = useState<CanSend>('askinput');
  const [mobileStatus, setMobileStatus] = useState<MobileStatus>(INITIAL_STATUS);
  // ADR-035: contact language — from the by-mobile lookup (existing contact) or
  // the Candidate language picker (new contact).
  const [contactLanguage, setContactLanguage] = useState<string | null>(null);
  const [contactLanguageSource, setContactLanguageSource] = useState<string | null>(null);
  // ADR-037: the company's WhatsApp senders decide whether the channel switch shows.
  // Looked up once per open; not a session field.
  const [senders, setSenders] = useState<WhatsAppSender[]>([]);
  const [channel, setChannel] = useState<NewMessageChannel>('sms');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    getWhatsAppSenders()
      .then((res) => { if (!cancelled) setSenders(res.senders); })
      .catch(() => { if (!cancelled) setSenders([]); });
    return () => { cancelled = true; };
  }, [open]);

  // Latest values for the async validator (it outlives several renders).
  const latest = useRef({ firstname, lastname, isContactExists, contactLanguageSource, contactLanguage });
  latest.current = { firstname, lastname, isContactExists, contactLanguageSource, contactLanguage };
  const lookupSeq = useRef(0);

  const reset = useCallback(() => {
    lookupSeq.current += 1;
    setTonumber('');
    setFirstname('');
    setLastname('');
    setIsContactExists(false);
    setExistingContactId(null);
    setCansend('askinput');
    setMobileStatus(INITIAL_STATUS);
    setContactLanguage(null);
    setContactLanguageSource(null);
    setChannel('sms');
  }, []);

  const validatePhone = useCallback(async (mobileNumber: string) => {
    const seq = ++lookupSeq.current;
    setCansend('checking');
    setMobileStatus({ status: 'validating', help: 'Checking number...' });

    try {
      const cansendRes = await checkCanSendNumber(mobileNumber);
      const contact = await getContactByMobile(mobileNumber);
      if (seq !== lookupSeq.current) return; // number changed or modal closed meanwhile

      const canSendTo = !!cansendRes.data.can_send;

      if (contact.data.length) {
        const [found] = contact.data;
        // ADR-035: a known language enables translate-before-send in the composer
        if (found.language) {
          setContactLanguage(found.language);
          setContactLanguageSource(found.language_source || 'detected');
        }
        setIsContactExists(true);
        setExistingContactId(found.id || null);
        if (found.contactDetails.length) {
          const first = _.find(found.contactDetails, { field: 'firstname' });
          const last = _.find(found.contactDetails, { field: 'lastname' });
          setFirstname(first ? first.value : latest.current.firstname);
          setLastname(last ? last.value : latest.current.lastname);
        } else {
          const name = parseFullName(found.name);
          setFirstname(name.first);
          setLastname(name.last);
        }
      }

      setCansend(canSendTo);
      setMobileStatus(canSendTo
        ? { status: 'success', help: null }
        : { status: 'error', help: 'We cannot send to this number.' });
    } catch {
      if (seq !== lookupSeq.current) return;
      setCansend(false);
      setMobileStatus({ status: 'error', help: 'Error checking number.' });
    }
  }, []);

  const debouncedValidatePhone = useMemo(() => _.debounce(validatePhone, 800), [validatePhone]);

  useEffect(() => () => debouncedValidatePhone.cancel(), [debouncedValidatePhone]);

  // Clear everything whenever the dialog closes, however it was closed.
  useEffect(() => {
    if (!open) {
      debouncedValidatePhone.cancel();
      reset();
    }
  }, [open, reset, debouncedValidatePhone]);

  const handlePhoneChange = useCallback((value: any) => {
    let mobileNumber = '';
    if (typeof value === 'object' && value !== null) {
      const { countryCode, areaCode, phoneNumber } = value;
      if (areaCode && phoneNumber) {
        mobileNumber = `+${countryCode || 1}${areaCode || ''}${phoneNumber || ''}`;
      }
    } else if (typeof value === 'string') {
      mobileNumber = value;
    }

    const wasExisting = latest.current.isContactExists;
    const manual = latest.current.contactLanguageSource === 'manual';

    setTonumber(mobileNumber);
    setCansend('askinput');
    setIsContactExists(false);
    setExistingContactId(null);
    if (wasExisting) {
      setFirstname('');
      setLastname('');
    }
    // a language that came from a previous lookup no longer applies to the new number
    if (!manual) {
      setContactLanguage(null);
      setContactLanguageSource(null);
    }
    setMobileStatus(INITIAL_STATUS);

    if (mobileNumber.length >= 10) {
      debouncedValidatePhone(mobileNumber);
    } else {
      lookupSeq.current += 1;
      debouncedValidatePhone.cancel();
    }
  }, [debouncedValidatePhone]);

  const phoneValid = cansend === true;

  const activeContactModel = phoneValid ? {
    id: 'new-message',
    name: `${firstname} ${lastname}`.trim() || 'Unknown',
    mobile: tonumber,
    blacklist: false,
    language: contactLanguage || null,
    language_source: contactLanguageSource || null,
  } : null;

  const sendMessage = async (
    messageText: string,
    _botmapId: any,
    _contactId: any,
    _mobile: any,
    moduleGroup?: string,
    translation?: any,
  ) => {
    const data = {
      to: tonumber,
      body: messageText,
      firstname: firstname || 'unknown',
      lastname: lastname || 'unknown',
      sendType: 'new-message',
      ...(moduleGroup ? { moduleGroup } : {}),
      // ADR-035: previewed translation metadata + the language for a brand-new contact
      ...(translation ? { translation } : {}),
      ...(!isContactExists && contactLanguage ? { language: contactLanguage } : {}),
    };
    await resources.sendReply(data);
    onClose();
  };

  const handleCalendarInvite = async (
    _mobile: any,
    _contactId: any,
    customMessage?: string,
    schedulingUserId?: string,
    translation?: any,
  ) => {
    await sendCalendarInvite({
      to: tonumber,
      firstname: firstname || 'unknown',
      lastname: lastname || 'unknown',
      // Forward the composed invite text and, for scheduling managers, the
      // chosen teammate's calendar — dropping them booked every invite on the sender.
      ...(customMessage ? { customMessage } : {}),
      ...(schedulingUserId ? { schedulingUserId } : {}),
      ...(translation ? { translation } : {}), // ADR-035
    });
    onClose();
  };

  // ADR-037: business-initiated WhatsApp = approved template + opt-in attestation.
  const sendWhatsAppTemplate = async (payload: WhatsAppTemplateSend) => {
    const data = {
      sendType: 'new-message',
      channel: 'whatsapp',
      to: tonumber,
      firstname: firstname || 'unknown',
      lastname: lastname || 'unknown',
      ...(!isContactExists && contactLanguage ? { language: contactLanguage } : {}),
      templateId: payload.templateId,
      ...(payload.templateKind ? { templateKind: payload.templateKind } : {}),
      inputs: payload.inputs,
      optInAttested: true,
      body: payload.body,
    };
    await resources.sendReply(data);
    message.success('WhatsApp template sent — you\'ll see replies in the inbox');
    onClose();
  };

  const userData = session.get('userData') || {};
  const showChannelSwitch = senders.length > 0;
  const whatsappSelected = showChannelSwitch && channel === 'whatsapp';

  return (
    <Modal
      title="New Message"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="tour-new-message-input-number">
          <Form>
            <Form.Item
              name="phone"
              label={<span style={{ fontWeight: 500 }}>Phone Number</span>}
              validateStatus={mobileStatus.status || undefined}
              help={mobileStatus.help}
              style={{ marginBottom: 0 }}
            >
              <PhoneInputUS
                enableSearch
                disabled={cansend === 'checking'}
                onChange={handlePhoneChange}
              />
            </Form.Item>
          </Form>
        </div>
        {showChannelSwitch && (
          <div data-testid="new-message-channel">
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Send via</div>
            <Segmented
              value={channel}
              onChange={(value) => setChannel(value as NewMessageChannel)}
              options={[
                { value: 'sms', label: <ChannelIcon channel="sms" withLabel /> },
                { value: 'whatsapp', label: <ChannelIcon channel="whatsapp" withLabel /> },
              ]}
            />
          </div>
        )}
        <div className="tour-new-message-name" style={{ display: 'flex', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, marginRight: 16 }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>First Name</div>
            <Input
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              disabled={isContactExists}
              autoComplete="off"
              placeholder="First name"
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Last Name</div>
            <Input
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              disabled={isContactExists}
              autoComplete="off"
              placeholder="Last name"
            />
          </div>
        </div>
        {/* ADR-035: Candidate language. Existing contacts bring their own (shown read-only
            when detected/manual); for a brand-new contact the recruiter can pick one, which
            enables translate-before-send and is saved on the contact as manual. */}
        {getRuntimeFeatures().hasFeature('ai-translation') && phoneValid && (
          <div>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>
              Candidate language
              <span style={{ color: '#8c8c8c', fontWeight: 400 }}> (optional — enables translated sending)</span>
            </div>
            {isContactExists && contactLanguage ? (
              <Tag icon={<TranslationOutlined />} color="blue" style={{ fontSize: 13, lineHeight: '22px' }}>
                {langName(contactLanguage)}
                {contactLanguageSource === 'detected' ? <span style={{ opacity: 0.7 }}> · detected</span> : null}
              </Tag>
            ) : (
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Same as mine"
                style={{ width: 260 }}
                value={contactLanguage || undefined}
                options={languageOptions(contactLanguage)}
                onChange={(value: string | undefined) => {
                  setContactLanguage(value || null);
                  setContactLanguageSource(value ? 'manual' : null);
                }}
              />
            )}
          </div>
        )}
        {whatsappSelected ? (
          <WhatsAppTemplatePanel
            firstName={firstname}
            lastName={lastname}
            userName={userData.name || ''}
            companyName={userData.brandName || userData.companyName || ''}
            phoneValid={phoneValid}
            contactId={existingContactId}
            onSend={sendWhatsAppTemplate}
          />
        ) : (
          <MessageInputField
            onCalendarSetup={onCalendarSetup}
            activeContactModel={activeContactModel}
            sendMessage={sendMessage}
            userMessageTemplates={templates}
            userSequenceTemplates={sequenceTemplates}
            userHasCalendar={userHasCalendar}
            sendCalendarInvite={handleCalendarInvite}
          />
        )}
      </div>
    </Modal>
  );
};

export default NewMessageModal;
